import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Columns3, Info, UserMinus, ExternalLink } from 'lucide-react';
import { appNutzer, type AusscheidenErgebnis } from '../api';

/**
 * Mitarbeiter des Kunden — wer über welche App verfügt.
 *
 * ══ NICHT DIE PORTALKONTEN ═════════════════════════════════════════════════
 *
 * Der Reiter „Benutzer" zeigt die **Portalkonten**: eine kaufmännische
 * Identität für Verträge, Rechnungen und Kündigung. Hier stehen die
 * **Arbeitszugänge** in open.junixx und bryve. Derselbe Mensch kann in einer,
 * in beiden oder in keiner Welt stehen — sie sind nicht verknüpft.
 *
 * ══ WOFÜR DIESE SEITE DA IST ═══════════════════════════════════════════════
 *
 * Für den Überblick und fürs **Ausscheiden**. Anlegen und feinere Rechte
 * gehören an die Instanz, weil sie dort in der Sprache der jeweiligen App
 * stehen — Mandanten bei open.junixx, Rollengruppe und Einheit bei bryve. Hier
 * wird nichts vereinheitlicht und nichts zwischen den Apps abgeglichen: wer in
 * open.junixx Geschäftsführung ist, kann in bryve Beobachter sein.
 *
 * ══ EINE ZEILE IST EINE ADRESSE, KEINE PERSON ══════════════════════════════
 *
 * Über die Apps hinweg gibt es keinen gemeinsamen Schlüssel ausser der E-Mail.
 * Geteilte Postfächer (`info@`, `lektorat@`) sind im CRM dieses Hauses der
 * Normalfall — dahinter stehen mehrere Menschen. Deshalb heisst die Spalte
 * „Adresse", und deshalb fragt das Ausscheiden vorher nach, mit der Liste
 * dessen, was geschlossen wird.
 */

const K = {
  karte: 'rounded-lg border border-slate-200 bg-surface',
  knopf: 'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium '
    + 'border border-slate-300 hover:bg-slate-50 '
    + 'disabled:opacity-50 disabled:cursor-not-allowed',
  th: 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500',
  td: 'px-3 py-2 text-sm align-top',
};

function Hinweis({ art, children }: { art: 'info' | 'warn'; children: React.ReactNode }) {
  const Symbol = art === 'warn' ? AlertTriangle : Info;
  const farbe = art === 'warn'
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : 'border-slate-200 bg-slate-50 text-slate-600';
  return (
    <div className={`flex gap-2 rounded-md border px-3 py-2 text-sm ${farbe}`}>
      <Symbol className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

const ZUSTAND_TEXT: Record<string, string> = {
  abgeschaltet: 'abgeschaltet',
  war_schon_aus: 'war schon aus',
  nicht_gefunden: 'kein Zugang dort',
  fehler: 'fehlgeschlagen',
};

export default function MitarbeiterPanel({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const [frage, setFrage] = useState<string | null>(null);
  const [grund, setGrund] = useState('');
  const [bericht, setBericht] = useState<AusscheidenErgebnis | null>(null);

  const abfrage = useQuery({
    queryKey: ['kunde-mitarbeiter', customerId],
    queryFn: () => appNutzer.uebersicht(customerId),
    retry: false,
  });

  const ausscheiden = useMutation({
    mutationFn: (email: string) => appNutzer.ausscheiden(customerId, email, grund.trim() || undefined),
    onSuccess: (daten) => {
      setBericht(daten);
      setFrage(null);
      setGrund('');
      qc.invalidateQueries({ queryKey: ['kunde-mitarbeiter', customerId] });
    },
  });

  const alleApps = abfrage.data?.apps ?? [];
  const zeilen = abfrage.data?.zeilen ?? [];

  /*
   * NUR DIE ERREICHBAREN BEKOMMEN EINE SPALTE.
   *
   * Eine Spalte voller „?" kostet Breite und sagt nichts, was nicht schon im
   * Hinweis darüber steht. Verschwiegen wird nichts: die stummen stehen
   * namentlich im eingeklappten Hinweis.
   */
  const erreichbare = alleApps.filter(a => a.erreichbar);

  /*
   * ══ WAS ANGEZEIGT WIRD, IST EINE ANSICHTSSACHE — WAS GESCHIEHT, NICHT ═════
   *
   * Ein Kunde kann viele Instanzen haben; wir selbst sind das Beispiel. Acht
   * Spalten sind unübersichtlich, zwanzig unlesbar. Deshalb lässt sich je
   * Instanz eine Spalte ausblenden.
   *
   * DAS AUSSCHEIDEN FOLGT DEM FILTER NICHT. Es schaltet den Zugang in ALLEN
   * Instanzen ab, auch in ausgeblendeten — alles andere wäre die gefährlichste
   * Sorte Bedienelement: eines, dessen Wirkung davon abhängt, was man gerade
   * sieht. Der Bestätigungsdialog zählt die ausgeblendeten deshalb ausdrücklich
   * mit auf.
   *
   * Die Auswahl liegt je Kunde im Browser. Sie ist eine Bequemlichkeit und
   * nichts, worauf sich jemand verlassen können muss — geht sie verloren, sind
   * eben wieder alle da.
   */
  const speicherName = `pc.mitarbeiter.spalten.${customerId}`;
  const [versteckt, setVersteckt] = useState<Set<string>>(() => {
    try {
      const roh = window.localStorage.getItem(speicherName);
      return new Set(roh ? (JSON.parse(roh) as string[]) : []);
    } catch {
      return new Set();
    }
  });

  const umschalten = (id: string) => {
    setVersteckt(alt2 => {
      const neu2 = new Set(alt2);
      if (neu2.has(id)) neu2.delete(id); else neu2.add(id);
      try { window.localStorage.setItem(speicherName, JSON.stringify([...neu2])); } catch { /* egal */ }
      return neu2;
    });
  };

  const apps = erreichbare.filter(a => !versteckt.has(a.instanceId));

  /** Die Zeile, über die gerade gefragt wird — für die Vorschau im Dialog. */
  const gefragt = useMemo(
    () => zeilen.find(z => z.email === frage) ?? null,
    [zeilen, frage],
  );

  if (abfrage.isError) {
    return <Hinweis art="warn">Die Übersicht liess sich nicht laden.</Hinweis>;
  }

  return (
    <div className="space-y-4">
      <Hinweis art="info">
        <strong>Arbeitszugänge</strong> in den Anwendungen dieses Kunden — nicht die
        Portalkonten unter „Benutzer" (Verträge, Rechnungen). Zugänge <em>anlegen</em> und
        feinere Rechte setzen geschieht an der jeweiligen Instanz, weil sie dort in der
        Sprache der App stehen.
      </Hinweis>

      {alleApps.some(a => !a.erreichbar) && (
        /* Eingeklappt, weil sie sonst die Tabelle verdraengt: bei acht Apps und
           sieben stummen sind das sieben Zeilen Vorspann vor der eigentlichen
           Auskunft. Die Zahl steht in der Zusammenfassung, das Warum eine
           Klickbewegung weiter. */
        <details className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <summary className="cursor-pointer select-none">
            <AlertTriangle className="mr-1 inline h-4 w-4 align-text-bottom" aria-hidden />
            {alleApps.filter(a => !a.erreichbar).length} von {alleApps.length} Instanzen antworten
            nicht — die übrigen Spalten stimmen trotzdem
          </summary>
          <ul className="mt-2 space-y-0.5 pl-5">
            {alleApps.filter(a => !a.erreichbar).map(a => (
              <li key={a.instanceId} className="list-disc">
                <strong>{a.stackName ?? a.produkt}</strong> — {a.meldung}
              </li>
            ))}
          </ul>
        </details>
      )}

      {bericht && (
        <div className={`${K.karte} p-3`}>
          <div className="mb-2 text-sm font-semibold">
            Ausgeschieden: {bericht.email}
          </div>
          <ul className="space-y-1 text-sm">
            {bericht.ergebnisse.map(e => (
              <li key={e.instanceId} className="flex gap-2">
                <span className={e.ok ? 'text-emerald-700' : 'text-amber-700'}>
                  {e.ok ? '✓' : '!'}
                </span>
                <span>
                  <strong>{e.stackName ?? e.produkt}</strong> — {ZUSTAND_TEXT[e.zustand] ?? e.zustand}
                  {e.meldung && <span className="text-slate-500"> · {e.meldung}</span>}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-slate-500">
            Steht hier ein „!", ist die Person dort <strong>nicht</strong> abgeschaltet —
            der Vorgang bricht bewusst nicht ab, sondern sagt, wo er hängen blieb.
          </p>
          <button type="button" className={`${K.knopf} mt-2`} onClick={() => setBericht(null)}>
            Schliessen
          </button>
        </div>
      )}

      {erreichbare.length > 1 && (
        <details className="rounded-md border border-slate-200 bg-surface px-3 py-2 text-sm" open={erreichbare.length <= 4}>
          <summary className="cursor-pointer select-none text-slate-600">
            <Columns3 className="mr-1 inline h-4 w-4 align-text-bottom" aria-hidden />
            Spalten — {apps.length} von {erreichbare.length} sichtbar
          </summary>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {erreichbare.map(a => (
              <label key={a.instanceId} className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={!versteckt.has(a.instanceId)}
                  onChange={() => umschalten(a.instanceId)}
                />
                {a.stackName ?? a.produkt}
                <span className="text-xs text-slate-400">{a.produkt}</span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Nur die Ansicht. <strong>Ausscheiden schaltet den Zugang in allen Instanzen ab</strong>
            {' '}— auch in den ausgeblendeten.
          </p>
        </details>
      )}

      <div className={`${K.karte} overflow-x-auto`}>
        <table className="w-full">
          <thead className="border-b border-slate-200">
            <tr>
              <th className={`${K.th} sticky left-0 z-20 bg-surface`}>Adresse</th>
              <th className={`${K.th} sticky z-20 bg-surface`} style={{ left: '13rem' }}>Name</th>
              {apps.map(a => (
                /* Produkt als kleine Marke NEBEN dem Namen statt als zweite
                   Zeile darunter: der Kopf bleibt einzeilig, und bei acht
                   Spalten sind das acht Zeilen weniger Hoehe. */
                <th className={`${K.th} whitespace-nowrap`} key={a.instanceId}>
                  <Link to={`/instances/${a.instanceId}`} className="inline-flex items-center gap-1.5 hover:underline">
                    {a.stackName ?? a.produkt}
                    <span className="rounded bg-slate-100 px-1 py-px text-[10px] font-normal normal-case text-slate-500">
                      {a.produkt}
                    </span>
                    <ExternalLink className="h-3 w-3 opacity-50" aria-hidden />
                  </Link>
                </th>
              ))}
              <th className={K.th} />
            </tr>
          </thead>
          <tbody>
            {zeilen.map(z => (
              /* Abwechselnd hinterlegt: bei acht Spalten verliert man sonst
                 auf halber Strecke die Zeile. */
              <tr key={z.email} className="border-b border-slate-100 odd:bg-slate-50/60">
                {/* Adresse und Name kleben, damit sie beim seitlichen Blaettern
                    stehen bleiben — ohne sie ist eine Zelle weit rechts nicht
                    zuzuordnen. */}
                <td className={`${K.td} sticky left-0 z-10 bg-inherit font-medium`}>{z.email}</td>
                <td className={`${K.td} sticky left-0 bg-inherit`} style={{ left: '13rem' }}>
                  {z.name ?? '—'}
                </td>
                {apps.map(a => {
                  const zugang = z.zugaenge.find(g => g.instanceId === a.instanceId);
                  if (!zugang) {
                    return <td className={`${K.td} text-slate-300`} key={a.instanceId}>—</td>;
                  }
                  return (
                    <td className={K.td} key={a.instanceId}>
                      {/* Die Rolle in der Wortwahl ihrer App. Nicht übersetzt. */}
                      <span className={zugang.aktiv ? '' : 'text-slate-400 line-through'}>
                        {zugang.rolle ?? 'Zugang'}
                      </span>
                      {!zugang.aktiv && <span className="ml-1 text-xs text-slate-500">aus</span>}
                    </td>
                  );
                })}
                <td className={`${K.td} text-right`}>
                  {/* Geschuetzte Adressen scheiden nicht aus — der Server lehnt es
                      ohnehin ab, und ein Knopf, der in eine Ablehnung laeuft, ist
                      schlechter als keiner. */}
                  {z.geschuetzt && (
                    <span className="text-xs text-slate-500">geschütztes Konto</span>
                  )}
                  {/* `z.zugaenge` ist ungefiltert — der Knopf erscheint also
                      auch, wenn der einzige aktive Zugang gerade ausgeblendet
                      ist. Genau so soll es sein. */}
                  {!z.geschuetzt && z.zugaenge.some(g => g.aktiv) && (
                    <button
                      type="button"
                      className={K.knopf}
                      onClick={() => { setFrage(z.email); setBericht(null); }}
                    >
                      <UserMinus className="h-4 w-4" aria-hidden /> Ausscheiden
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {zeilen.length === 0 && !abfrage.isFetching && (
              <tr>
                <td colSpan={apps.length + 3} className="px-3 py-6 text-center text-sm text-slate-500">
                  Keine Arbeitszugänge gefunden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {gefragt && (
        <div className={`${K.karte} space-y-3 border-amber-300 p-3`}>
          <div className="text-sm font-semibold">
            Alle Zugänge von {gefragt.email} schliessen?
          </div>

          {/*
            * DIE VORSCHAU IST DER EIGENTLICHE SCHUTZ.
            *
            * Zusammengeführt wird über die Adresse, und ein geteiltes Postfach
            * ist mehrere Menschen. Wer hier auf „lektorat@…" klickt, soll vorher
            * sehen, was er zumacht — nicht danach.
            */}
          {/*
            * AUS `alleApps` NACHGESCHLAGEN, NICHT AUS `apps`.
            *
            * `apps` ist die gefilterte Ansicht. Wer hier nachschlüge, bekäme für
            * eine ausgeblendete Instanz `undefined` und zeigte eine Zeile ohne
            * Namen — bei genau dem Dialog, der sagen soll, was gleich passiert.
            */}
          <ul className="space-y-1 text-sm">
            {gefragt.zugaenge.filter(g => g.aktiv).map(g => {
              const app = alleApps.find(a => a.instanceId === g.instanceId);
              const ausgeblendet = versteckt.has(g.instanceId);
              return (
                <li key={g.instanceId}>
                  <strong>{app?.stackName ?? app?.produkt ?? g.instanceId.slice(0, 8)}</strong>
                  {g.rolle && <span className="text-slate-500"> · {g.rolle}</span>}
                  {ausgeblendet && (
                    <span className="ml-1 rounded bg-slate-100 px-1 py-px text-[10px] text-slate-500">
                      ausgeblendet
                    </span>
                  )}
                </li>
              );
            })}
          </ul>

          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Grund (steht im Prüfbericht, nicht in der Anwendung)
            </span>
            <input
              className="w-full rounded-md border border-slate-300 bg-surface px-2.5 py-1.5 text-sm"
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
              placeholder="z. B. ausgeschieden zum 30.09."
            />
          </label>

          <p className="text-xs text-slate-500">
            Abschalten nimmt den Zugang und lässt den Datensatz stehen — Aufgaben, Zeiten
            und Kommentare bleiben zugeordnet. Rückgängig geht es an der jeweiligen Instanz.
            {gefragt.zugaenge.some(g => g.aktiv && versteckt.has(g.instanceId)) && (
              <> <strong>Die oben als „ausgeblendet" markierten sind mit dabei</strong> —
              der Spaltenfilter ändert nur die Ansicht.</>
            )}
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              className={`${K.knopf} font-semibold`}
              disabled={ausscheiden.isPending}
              onClick={() => ausscheiden.mutate(gefragt.email)}
            >
              Zugänge schliessen
            </button>
            <button type="button" className={K.knopf} onClick={() => setFrage(null)}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Jede Spalte zeigt die Rolle so, wie ihre Anwendung sie nennt. Zwischen den Apps wird
        nichts abgeglichen — die Rechtemodelle sind voneinander unabhängig.
      </p>
    </div>
  );
}
